import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, BellRing, CheckCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ApprovalReviewDialog from "@/components/ApprovalReviewDialog";

export default function NotificationsPage() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [reviewOpen, setReviewOpen] = useState(false);
  const load = async () => setRows((await api.get("/notifications", { params: filter ? { status: filter } : {} })).data);
  useEffect(() => { load(); }, [filter]);

  const pendingCount = useMemo(() => rows.filter((n) => n.status === "pending").length, [rows]);

  const approve = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/approve`);
      // Friendly per-entity-type confirmation
      const map = {
        worker_join: "Worker added — they're now in their client switcher",
        medical_staff: "Treating staff change applied",
        roster: n.action === "delete" ? "Removed from the roster" : "Roster change applied",
        timesheet: "Timesheet approved",
        invoice: "Invoice approved",
        vital: "Vital signs approved",
        incident: "Incident approved",
        kilometre: "Kilometres approved",
      };
      toast.success(map[n.entity_type] || "Approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (n) => { try { await api.post(`/notifications/${n.id}/reject`); toast("Rejected"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="notifications-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight flex items-center gap-3">
            <BellRing className="w-8 h-8 text-coral" /> Approvals
          </h1>
          <p className="text-muted-foreground">Review every change submitted by your support workers.</p>
        </div>
        {pendingCount > 0 && filter === "pending" && (
          <Button
            onClick={() => setReviewOpen(true)}
            data-testid="approve-all-btn"
            className="rounded-full font-bold gap-2 min-h-[48px]"
            style={{ background: "hsl(var(--client))" }}
          >
            <CheckCheck className="w-4 h-4" />
            Review &amp; approve ({pendingCount})
          </Button>
        )}
      </header>

      <ApprovalReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        onApproved={load}
      />

      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "rejected", ""].map((s) => (
          <Button key={s || "all"} size="sm" variant={filter === s ? "default" : "outline"} className="rounded-full font-bold capitalize"
            onClick={() => setFilter(s)} data-testid={`notif-filter-${s || "all"}`}>{s || "all"}</Button>
        ))}
      </div>

      <div className="space-y-3">
        {rows.map((n) => (
          <Card key={n.id} className="rounded-3xl p-5 border-2" data-testid={`notif-${n.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="rounded-full uppercase font-bold tracking-wider text-[10px] capitalize" variant="outline">{n.action} · {n.entity_type}</Badge>
                  <Badge className={`rounded-full uppercase font-bold tracking-wider text-[10px] ${
                    n.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                    n.status === "rejected" ? "bg-destructive/10 text-destructive" : "bg-amber-100 text-amber-800"
                  }`}>{n.status}</Badge>
                  <span className="text-xs text-muted-foreground">{n.created_at?.slice(0, 16).replace("T", " ")}</span>
                </div>
                <p className="font-bold mt-2">{n.summary}</p>
                <p className="text-xs text-muted-foreground">By {n.actor_name}</p>
              </div>
              {n.status === "pending" && (
                <div className="flex gap-2">
                  <Button onClick={() => approve(n)} className="rounded-full font-bold gap-1" style={{ background: "hsl(var(--client))" }} data-testid={`approve-${n.id}`}>
                    <Check className="w-4 h-4" /> Approve
                  </Button>
                  <Button onClick={() => reject(n)} variant="outline" className="rounded-full font-bold gap-1" data-testid={`reject-${n.id}`}>
                    <X className="w-4 h-4" /> Reject
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card className="rounded-3xl p-10 text-center text-muted-foreground border-2">All caught up — no items to review.</Card>}
      </div>
    </div>
  );
}
